/**
 * Vista previa editable de la lámina.
 *
 * Lo que se ve acá es **exactamente** lo que se descarga: usa el mismo motor de
 * composición, no una aproximación. Una previa que no coincide con el entregable
 * es peor que no tener previa, porque genera confianza en algo que no se verificó.
 *
 * Dos costos muy distintos, y de ahí sale toda la arquitectura:
 *
 *   - Cambiar el ZOOM obliga a re-renderizar el raster del mapa: hay que mover
 *     la vista, esperar teselas y volver a pintar. Es caro y va con debounce.
 *   - Mover o redimensionar un CUADRO sólo re-compone la lámina sobre el mismo
 *     raster ya renderizado. Es barato y corre en cada frame del arrastre.
 *
 * Las manijas son elementos del DOM, no dibujo del canvas. Así nunca terminan
 * dentro del PDF: lo que se descarga es el canvas tal cual.
 */

const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const ZOOM_STEPS = 6;

export function openPreview({ renderSource, compose, download, onClose }) {
  const state = {
    zoomDelta: 0,
    boxScale: 1,
    boxes: {},
    source: null,
    canvas: null,
  };

  // ---------------------------------------------------------------- estructura

  const overlay = document.createElement('div');
  overlay.className = 'preview-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', 'Vista previa de la lámina');

  const bar = document.createElement('div');
  bar.className = 'preview-bar';

  const title = document.createElement('span');
  title.className = 'preview-title';
  title.textContent = 'Vista previa · así saldrá el PDF';

  const status = document.createElement('span');
  status.className = 'preview-status';

  const controls = document.createElement('div');
  controls.className = 'preview-controls';

  const stage = document.createElement('div');
  stage.className = 'preview-stage';

  const holder = document.createElement('div');
  holder.className = 'preview-holder';
  stage.appendChild(holder);

  const button = (label, hint, onClick) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.title = hint;
    b.addEventListener('click', onClick);
    controls.appendChild(b);
    return b;
  };

  bar.append(title, status, controls);
  overlay.append(bar, stage);

  // ------------------------------------------------------------------ dibujado

  let busy = false;
  let pendingZoom = null;

  const setStatus = (text) => {
    status.textContent = text;
  };

  /** Re-compone sobre el raster ya renderizado. Barato: se usa al arrastrar. */
  function recompose() {
    if (!state.source) return;
    const canvas = compose(state.source, {
      boxes: state.boxes,
      boxScale: state.boxScale,
    });
    if (state.canvas) state.canvas.replaceWith(canvas);
    else holder.appendChild(canvas);
    canvas.className = 'preview-canvas';
    state.canvas = canvas;
    placeHandles();
  }

  /** Re-renderiza el mapa. Caro: sólo cuando cambia el zoom. */
  async function rerender() {
    if (busy) {
      pendingZoom = state.zoomDelta;
      return;
    }
    busy = true;
    setStatus('Preparando…');
    try {
      state.source = await renderSource(state.zoomDelta);
      recompose();
      setStatus('');
    } catch (error) {
      setStatus(`No se pudo preparar la vista: ${error.message}`);
    } finally {
      busy = false;
      if (pendingZoom !== null && pendingZoom !== state.zoomDelta) {
        pendingZoom = null;
        rerender();
      } else {
        pendingZoom = null;
      }
    }
  }

  // ------------------------------------------------------------------- manijas

  const handles = [];

  /** Escala entre las unidades de la lámina y los píxeles mostrados en pantalla. */
  function displayScale() {
    if (!state.canvas || !state.canvas.clientWidth) return 1;
    const sheetWidth = state.canvas.width / 2; // EXPORT_SCALE
    return state.canvas.clientWidth / sheetWidth;
  }

  function clearHandles() {
    handles.forEach((h) => h.remove());
    handles.length = 0;
  }

  function placeHandles() {
    clearHandles();
    const rects = state.canvas?.boxRects || [];
    const k = displayScale();
    const offsetLeft = state.canvas.offsetLeft;
    const offsetTop = state.canvas.offsetTop;

    rects.forEach((rect) => {
      const frame = document.createElement('div');
      frame.className = 'preview-box';
      frame.style.left = `${offsetLeft + rect.x * k}px`;
      frame.style.top = `${offsetTop + rect.y * k}px`;
      frame.style.width = `${rect.w * k}px`;
      frame.style.height = `${rect.h * k}px`;
      frame.title = 'Arrastra para mover · toma la esquina para redimensionar';

      const grip = document.createElement('div');
      grip.className = 'preview-grip';
      grip.title = 'Redimensionar';
      frame.appendChild(grip);

      attachDrag(frame, grip, rect);
      holder.appendChild(frame);
      handles.push(frame);
    });
  }

  function attachDrag(frame, grip, rect) {
    let mode = null;
    let startX = 0;
    let startY = 0;
    let originX = 0;
    let originY = 0;
    let originScale = 1;

    const begin = (event, nextMode) => {
      mode = nextMode;
      startX = event.clientX;
      startY = event.clientY;
      const current = state.boxes[rect.index] || {};
      originX = current.x ?? rect.x;
      originY = current.y ?? rect.y;
      originScale = current.scale ?? rect.scale ?? 1;
      frame.setPointerCapture?.(event.pointerId);
      event.preventDefault();
      event.stopPropagation();
    };

    const move = (event) => {
      if (!mode) return;
      const k = displayScale() || 1;
      const dx = (event.clientX - startX) / k;
      const dy = (event.clientY - startY) / k;

      if (mode === 'move') {
        state.boxes[rect.index] = { ...state.boxes[rect.index], x: originX + dx, y: originY + dy };
      } else {
        // Se escala por el ancho: usar la diagonal haría que un tirón vertical
        // en un cuadro ancho lo agrandara desproporcionadamente.
        const factor = 1 + dx / Math.max(40, rect.w / (rect.scale || 1));
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, originScale * factor));
        state.boxes[rect.index] = {
          ...state.boxes[rect.index],
          x: originX,
          y: originY,
          scale,
        };
      }
      recompose();
    };

    const end = () => {
      mode = null;
    };

    frame.addEventListener('pointerdown', (event) => {
      if (event.target === grip) return;
      begin(event, 'move');
    });
    grip.addEventListener('pointerdown', (event) => begin(event, 'resize'));
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);

    frame._detach = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
    };
  }

  // ------------------------------------------------------------------ controles

  button('− mapa', 'Alejar el mapa', () => {
    state.zoomDelta = Math.max(-ZOOM_STEPS, state.zoomDelta - 1);
    rerender();
  });
  button('+ mapa', 'Acercar el mapa', () => {
    state.zoomDelta = Math.min(ZOOM_STEPS, state.zoomDelta + 1);
    rerender();
  });
  button('− cuadros', 'Achicar todos los cuadros', () => {
    state.boxScale = Math.max(MIN_SCALE, state.boxScale - 0.1);
    state.boxes = {};
    recompose();
  });
  button('+ cuadros', 'Agrandar todos los cuadros', () => {
    state.boxScale = Math.min(MAX_SCALE, state.boxScale + 0.1);
    state.boxes = {};
    recompose();
  });
  button('Reiniciar', 'Volver al encuadre y los tamaños por defecto', () => {
    state.zoomDelta = 0;
    state.boxScale = 1;
    state.boxes = {};
    rerender();
  });

  const pngButton = button('Descargar PNG', 'Descargar la lámina como imagen', () =>
    download(state.canvas, 'png'),
  );
  pngButton.className = 'primary';

  const pdfButton = button('Descargar PDF', 'Descargar la lámina lista para imprimir', async () => {
    pdfButton.disabled = true;
    setStatus('Generando PDF…');
    try {
      await download(state.canvas, 'pdf');
      setStatus('');
    } catch (error) {
      setStatus(`No se pudo generar el PDF: ${error.message}`);
    } finally {
      pdfButton.disabled = false;
    }
  });
  pdfButton.className = 'primary';

  button('Cerrar', 'Volver al mapa', () => close());

  // ------------------------------------------------------------------- ciclo

  const onKeyDown = (event) => {
    if (event.key === 'Escape') close();
  };

  function close() {
    handles.forEach((h) => h._detach?.());
    clearHandles();
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', placeHandles);
    overlay.remove();
    onClose?.();
  }

  document.addEventListener('keydown', onKeyDown);
  window.addEventListener('resize', placeHandles);
  document.body.appendChild(overlay);
  rerender();

  return { close };
}
